import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import { deleteRequest, getRequest, patchRequest, postRequest } from '../../utils/axiosRequests';

const ELEMENT_TYPES = [
  'chiefComplain',
  'diagnosis',
  'observation',
  'investigation',
  'advice',
  'referredto',
  'treatmentPlan',
  'history',
  'surgicalHistory',
  'oncologyHistory',
  'eyeExamination',
  'note',
  'physicalTherapy',
  'exercise',
  'orthosis',
  'infertility',
  'urologyExamination',
  'systemicExamination',
  'gyneExamination',
  'breastExamination',
  'earNoseThroths',
  'mentalStateExamination',
  'nervousSystem',
];

const emptyForm = { elementType: '', itemElement: '', note: '', priority: 0 };

function ItemElementNotes() {
  const [notes, setNotes] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 30;

  const [filterType, setFilterType] = useState('');
  const [filterElement, setFilterElement] = useState('');
  const [appliedType, setAppliedType] = useState('');
  const [appliedElement, setAppliedElement] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [addForm, setAddForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const fetchNotes = async (page = 1, append = false) => {
    const params = new URLSearchParams({ page, perPage });
    if (appliedType) params.append('elementType', appliedType);
    if (appliedElement) params.append('itemElement', appliedElement);
    try {
      const data = await getRequest(`prescription-item-element-notes?${params}`);
      const items = Array.isArray(data) ? data : data?.data || data;
      if (append) {
        setNotes((prev) => [...prev, ...(Array.isArray(items) ? items : [])]);
      } else {
        setNotes(Array.isArray(items) ? items : []);
        setTotalItem(data?.total || (Array.isArray(data) ? data.length : 0));
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchNotes(1, false);
  }, [appliedType, appliedElement]);

  useEffect(() => {
    if (currentPage > 1) fetchNotes(currentPage, true);
  }, [currentPage]);

  const handleFilter = (e) => {
    e.preventDefault();
    setAppliedType(filterType);
    setAppliedElement(filterElement);
  };

  const handleClear = () => {
    setFilterType('');
    setFilterElement('');
    setAppliedType('');
    setAppliedElement('');
  };

  const handleAdd = async () => {
    if (!addForm.elementType || !addForm.itemElement || !addForm.note) return;
    setIsSaving(true);
    try {
      await postRequest('prescription-item-element-notes', {
        ...addForm,
        priority: Number(addForm.priority) || 0,
      });
      setShowAddModal(false);
      setAddForm(emptyForm);
      fetchNotes(1, false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditOpen = (item) => {
    setEditItem(item);
    setEditForm({
      elementType: item.elementType || '',
      itemElement: item.itemElement || '',
      note: item.note || '',
      priority: item.priority ?? 0,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editItem) return;
    setIsSaving(true);
    try {
      const updated = await patchRequest(
        `prescription-item-element-notes/${editItem.id || editItem._id}`,
        { ...editForm, priority: Number(editForm.priority) || 0 },
      );
      setNotes((prev) =>
        prev.map((n) =>
          (n.id || n._id) === (editItem.id || editItem._id) ? updated : n,
        ),
      );
      setShowEditModal(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteOpen = (item) => {
    setEditItem(item);
    setShowConfirmModal(true);
  };

  const handleDelete = async () => {
    if (!editItem) return;
    setIsSaving(true);
    try {
      await deleteRequest(
        `prescription-item-element-notes/${editItem.id || editItem._id}`,
      );
      setNotes((prev) =>
        prev.filter(
          (n) => (n.id || n._id) !== (editItem.id || editItem._id),
        ),
      );
      setTotalItem((t) => t - 1);
      setShowConfirmModal(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <Form className="mb-3" onSubmit={handleFilter}>
        <Row className="align-items-end g-2">
          <Col xs={4} sm={3}>
            <Form.Select
              size="sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              {ELEMENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={5} sm={5}>
            <Form.Control
              size="sm"
              type="text"
              placeholder="Filter by item element..."
              value={filterElement}
              onChange={(e) => setFilterElement(e.target.value)}
            />
          </Col>
          <Col xs="auto">
            <Button variant="outline-success" size="sm" type="submit">
              <i className="fa fa-filter" /> Filter
            </Button>
          </Col>
          <Col xs="auto">
            <Button variant="outline-danger" size="sm" type="button" onClick={handleClear}>
              <i className="fa fa-redo" /> Clear
            </Button>
          </Col>
          <Col xs="auto" className="ms-auto">
            <Button
              variant="success"
              size="sm"
              onClick={() => { setAddForm(emptyForm); setShowAddModal(true); }}
            >
              <i className="fa fa-plus-circle" /> Add Note
            </Button>
          </Col>
        </Row>
      </Form>

      <div style={{ fontSize: '.8rem', color: '#666', marginBottom: '6px' }}>
        Showing {notes.length}{totalItem > 0 ? ` of ${totalItem}` : ''} notes
      </div>

      <Table hover size="sm">
        <thead>
          <tr>
            <th style={{ width: '16%' }}>Element Type</th>
            <th style={{ width: '22%' }}>Item Element</th>
            <th>Note</th>
            <th style={{ width: '7%', textAlign: 'center' }}>Priority</th>
            <th style={{ width: '8%', textAlign: 'right' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {notes.map((item) => (
            <tr key={item.id || item._id}>
              <td>
                <span style={{ fontSize: '.8rem', color: '#555', fontStyle: 'italic' }}>
                  {item.elementType}
                </span>
              </td>
              <td style={{ fontWeight: 500 }}>{item.itemElement}</td>
              <td style={{ color: '#3e3838' }}>{item.note}</td>
              <td style={{ textAlign: 'center' }}>{item.priority ?? 0}</td>
              <td style={{ textAlign: 'right' }}>
                <i
                  className="fa fa-edit"
                  style={{ cursor: 'pointer', color: '#247e92' }}
                  onClick={() => handleEditOpen(item)}
                />
                <i
                  className="far fa-trash-alt v-error"
                  style={{ cursor: 'pointer', marginLeft: '10px' }}
                  onClick={() => handleDeleteOpen(item)}
                />
              </td>
            </tr>
          ))}
          {notes.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center text-muted py-3">
                No notes found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {notes.length < totalItem && (
        <Button size="sm" onClick={() => setCurrentPage((p) => p + 1)}>
          Load More
        </Button>
      )}

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header className="common-modal-header">
          <Modal.Title>Add Note</Modal.Title>
          <i
            className="far fa-times-circle cursor-pointer"
            onClick={() => setShowAddModal(false)}
          />
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Element Type</Form.Label>
            <Form.Select
              size="sm"
              value={addForm.elementType}
              onChange={(e) => setAddForm({ ...addForm, elementType: e.target.value })}
            >
              <option value="">Select type...</option>
              {ELEMENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Item Element</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              placeholder="e.g. Fever, LUCS, family..."
              value={addForm.itemElement}
              onChange={(e) => setAddForm({ ...addForm, itemElement: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Note</Form.Label>
            <Form.Control
              size="sm"
              as="textarea"
              rows={3}
              value={addForm.note}
              onChange={(e) => setAddForm({ ...addForm, note: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Priority</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              value={addForm.priority}
              onChange={(e) => setAddForm({ ...addForm, priority: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={isSaving || !addForm.elementType || !addForm.itemElement || !addForm.note}
            onClick={handleAdd}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header className="common-modal-header">
          <Modal.Title>Edit Note</Modal.Title>
          <i
            className="far fa-times-circle cursor-pointer"
            onClick={() => setShowEditModal(false)}
          />
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Element Type</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={editForm.elementType}
              onChange={(e) => setEditForm({ ...editForm, elementType: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Item Element</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={editForm.itemElement}
              onChange={(e) => setEditForm({ ...editForm, itemElement: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Note</Form.Label>
            <Form.Control
              size="sm"
              as="textarea"
              rows={3}
              value={editForm.note}
              onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Priority</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              value={editForm.priority}
              onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" disabled={isSaving} onClick={handleUpdate}>
            Update
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header className="common-modal-header">
          <Modal.Title>Delete Note</Modal.Title>
          <i
            className="far fa-times-circle cursor-pointer"
            onClick={() => setShowConfirmModal(false)}
          />
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the note for{' '}
          <strong>{editItem?.itemElement}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" disabled={isSaving} onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ItemElementNotes;
